import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip, Tooltip, Typography } from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import {
    appDataGridSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../Helpers/dataGridStyles.js";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import useServerGridSort from "../Helpers/useServerGridSort.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import GridRowContextMenu, {
    buildOrderShareUrl,
    copyTextToClipboard,
    useGridRowContextMenu,
} from "../Helpers/GridRowContextMenu.jsx";
import CancelOrderDialog from "./CancelOrderDialog.jsx";
import "../Products/catalog.css";

function isCancellableStatus(status) {
    const key = String(status || "").toLowerCase();
    if (!key) return true;
    if (key.includes("cancel")) return false;
    if (/(fulfilled|shipped|delivered)/.test(key)) return false;
    return true;
}

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function money(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return currencyFormatter(n, 2);
}

function dash(value) {
    return value == null || value === "" ? "—" : value;
}

function statusChipSx(status) {
    const key = String(status || "").toLowerCase();
    const base = {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
    };
    if (key.includes("cancel")) {
        return { ...base, backgroundColor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
    }
    if (/(fulfilled|shipped|delivered)/.test(key)) {
        return { ...base, backgroundColor: "#ecfdf5", color: "#047857", border: "1px solid #6ee7b7" };
    }
    if (/(not started|created|pending)/.test(key)) {
        return { ...base, backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74" };
    }
    if (/(in progress|acknowledged|requested)/.test(key)) {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    const base = {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
    };
    if (key === "ebay") {
        return {
            ...base,
            backgroundColor: "#fef3c7",
            color: "#854d0e",
            border: "1px solid #fcd34d",
        };
    }
    if (key === "walmart") {
        return {
            ...base,
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            border: "1px solid #93c5fd",
        };
    }
    if (key === "shopify") {
        return {
            ...base,
            backgroundColor: "#ecfdf5",
            color: "#047857",
            border: "1px solid #6ee7b7",
        };
    }
    if (key.includes("facebook")) {
        return {
            ...base,
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
        };
    }
    return {
        ...base,
        backgroundColor: "#f1f5f9",
        color: "#475569",
    };
}

export default function AllOrdersGrid({
    orders,
    loading,
    onCancelled,
    paginationMode = "client",
    rowCount,
    paginationModel,
    onPaginationModelChange,
    onSortForApi,
}) {
    const [searchParams] = useSearchParams();
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelOrder, setCancelOrder] = useState(null);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const shareTab = searchParams.get("t") || "0";
    const rowMenuActions = useMemo(
        () => [
            {
                id: "copyShareUrl",
                label: "Copy share URL",
                disabled: (row) => !String(row?.orderNumber || "").trim(),
                onClick: async (row) => {
                    await copyTextToClipboard(
                        buildOrderShareUrl(row, { tab: shareTab })
                    );
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    });
                },
            },
        ],
        [shareTab]
    );

    const rows = useMemo(
        () =>
            (orders || []).map((order) => {
                const f = order.fields || {};
                const channel = f["Selling channel"] || "";
                const orderNumber =
                    f["Order Number"] || f["Walmart Customer Order ID"] || "";
                return {
                    id: order.id,
                    orderNumber,
                    // Marketplace cancel APIs expect the channel order id (PO / eBay order id).
                    cancelOrderNumber: f["Order Number"] || orderNumber,
                    channel,
                    status: f["Order Status"] || "",
                    platformStatus: f["Platform Status"] || "",
                    orderDate: formatDate(f["Order Creation Date"]),
                    itemId: f["Item ID"] || f["SKU Helper"] || "",
                    itemTitle: f["Item Title"] || "",
                    buyerName: f["Buyer Name"] || "",
                    buyerPendingOrderCount: Math.max(
                        1,
                        Math.floor(Number(order.buyerPendingOrderCount) || 1)
                    ),
                    city: f["Ship to city"] || "",
                    state: f["Ship to province/region/state"] || "",
                    zip: f["Ship to zip"] || "",
                    quantity: f["Quantity"] ?? "",
                    assignedUnits: f["Assigned Units"] ?? 0,
                    catalogItemIds: f["Catalog Item IDs"] || "",
                    itemPrice: f["Item price"],
                    gross: f["Gross amount"],
                    contributionMargin: f["Contribution Margin"],
                    netEarnings: f["Net Earnings"],
                    shippingService: f["Shipping Service"] || "",
                    tracking: f["Shipping Tracking Number"] || "",
                };
            }),
        [orders]
    );

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const layout = useGridLayout(GRID_LAYOUT_KEYS.orders, {
        sortModel: [{ field: "orderDate", sort: "desc" }],
        columnVisibilityModel: {},
    });
    const { sortModel, sortForApi, onSortModelChange } = useServerGridSort(
        layout,
        "orderDate",
        "desc"
    );

    useEffect(() => {
        if (typeof onSortForApi !== "function") return;
        onSortForApi(sortForApi);
    }, [sortForApi, onSortForApi]);

    const openCancel = (row) => {
        setCancelOrder({
            id: row.id,
            orderChannel: row.channel,
            customerOrderNumber: row.cancelOrderNumber || row.orderNumber,
            quantity: row.quantity,
        });
        setCancelOpen(true);
    };

    const closeCancel = async (didCancel) => {
        setCancelOpen(false);
        setCancelOrder(null);
        if (didCancel) {
            await onCancelled?.();
        }
    };

    const baseColumns = useMemo(
        () => [
            { field: "orderNumber", headerName: "Order Number", minWidth: 170, flex: 0.8 },
            {
                field: "channel",
                headerName: "Selling channel",
                minWidth: 130,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) return dash(text);
                    return (
                        <Chip
                            size="small"
                            label={text}
                            sx={channelChipSx(text)}
                        />
                    );
                },
            },
            {
                field: "status",
                headerName: "Order Status",
                minWidth: 150,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Chip
                            size="small"
                            label={text}
                            sx={statusChipSx(text)}
                        />
                    );
                },
            },
            {
                field: "platformStatus",
                headerName: "Platform Status",
                minWidth: 160,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return <Chip size="small" label={text} sx={statusChipSx(text)} />;
                },
            },
            { field: "orderDate", headerName: "Order Creation Date", minWidth: 150 },
            { field: "itemId", headerName: "Item ID", minWidth: 140 },
            { field: "itemTitle", headerName: "Item Title", minWidth: 240, flex: 1.4 },
            {
                field: "buyerName",
                headerName: "Buyer Name",
                minWidth: 150,
                renderCell: (params) => {
                    const name = params.value || "";
                    const pendingCount = Number(params.row.buyerPendingOrderCount) || 1;
                    const status = String(params.row.status || "").toLowerCase();
                    const showMulti =
                        pendingCount > 1 &&
                        (status.includes("pending") || status.includes("not started"));
                    if (!showMulti) return name || "";
                    return (
                        <Tooltip title={`Buyer has ${pendingCount} orders to ship`}>
                            <span style={{ color: "#b45309", fontWeight: 600 }}>
                                {name || ""}
                            </span>
                        </Tooltip>
                    );
                },
            },
            { field: "city", headerName: "Ship to city", minWidth: 130 },
            { field: "state", headerName: "State", minWidth: 90 },
            { field: "zip", headerName: "Zip", width: 90 },
            { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
            {
                field: "catalogItemIds",
                headerName: "Assigned Item",
                minWidth: 140,
                flex: 0.6,
                sortable: false,
                valueGetter: (_value, row) => row.catalogItemIds || "—",
            },
            {
                field: "itemPrice",
                headerName: "Item price",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => money(value) || "—",
            },
            {
                field: "gross",
                headerName: "Gross amount",
                width: 120,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => money(value) || "—",
            },
            {
                field: "contributionMargin",
                headerName: "Contribution Margin",
                width: 150,
                align: "right",
                headerAlign: "right",
                sortable: false,
                valueFormatter: (value) => money(value) || "—",
            },
            {
                field: "netEarnings",
                headerName: "Net Earnings",
                width: 130,
                align: "right",
                headerAlign: "right",
                sortable: false,
                valueFormatter: (value) => money(value) || "—",
            },
            { field: "shippingService", headerName: "Shipping Service", minWidth: 150 },
            { field: "tracking", headerName: "Tracking Number", minWidth: 160 },
            {
                field: "cancel",
                headerName: "Cancel",
                minWidth: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const canCancel =
                        isCancellableStatus(params.row.status) &&
                        isCancellableStatus(params.row.platformStatus);
                    if (!canCancel) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 12 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<CancelOutlinedIcon fontSize="small" />}
                            onClick={(event) => {
                                event.stopPropagation();
                                openCancel(params.row);
                            }}
                            sx={{
                                backgroundColor: "#fee2e2",
                                color: "#991b1b",
                                height: 28,
                                fontSize: 12,
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#fecaca" },
                            }}
                        >
                            Cancel
                        </Button>
                    );
                },
            },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    return (
        <div className="catalog-grid-shell">
            <div className="catalog-toolbar">
                <div className="catalog-toolbar-spacer" />
                <GridLayoutControls layout={layout} columns={baseColumns} />
            </div>
            <div className="catalog-grid-main">
                <DataGrid
                    {...appDataGridDefaultProps}
                    rowHeight={48}
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    sortingMode={paginationMode === "server" ? "server" : "client"}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    columnVisibilityModel={layout.columnVisibilityModel}
                    onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                    onColumnWidthChange={layout.onColumnWidthChange}
                    pageSizeOptions={[25, 50, 100]}
                    paginationMode={paginationMode}
                    rowCount={paginationMode === "server" ? rowCount : undefined}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChange}
                    initialState={
                        paginationModel
                            ? undefined
                            : { pagination: { paginationModel: { pageSize: 25 } } }
                    }
                    slotProps={gridSlotProps}
                    sx={{
                        ...appDataGridSx,
                        "& .MuiDataGrid-cellContent": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        },
                    }}
                />
            </div>
            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />
            <CancelOrderDialog
                isOpen={cancelOpen}
                onClose={() => closeCancel(false)}
                onSuccess={() => closeCancel(true)}
                orderChannel={cancelOrder?.orderChannel}
                orderId={cancelOrder}
            />
            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}
        </div>
    );
}
